'use client';

import { useEffect } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { usePreservedCallback } from '@toss/react';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { createPaymentFailureDialog } from '../../payment-tracking/PaymentFailureContent';
import { PaypleProvider, usePayple } from '../hooks';
import { AdditionalPaymentFormProvider } from '../paymentForm/AdditionalPaymentFormProvider';
import { InputCardNameModal } from '../paymentForm/InputCardNameModal';
import { PlanTitle } from './atoms';
import { PaymentLoading, PaymentPaypleCardRegister, PaymentPeriodRadio, PaymentSummary, PlanCheckoutSpace, PlanSelectMaxCcu, SpacePlanSelectButtons, SubscriptionPaymentSteps } from './components';
import { useSubscriptionPaymentContext } from './hooks';
import S from './SubscriptionPayment.module.scss';
import { SubscriptionPaymentProvider, SubscriptionPaymentProviderProps } from './SubscriptionPaymentProvider';
export const Content = () => {
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isSuccess,
    isFailure,
    failMessage,
    failureMetadata,
    failureCount
  } = usePayple();
  const inputCardNameModal = useModal(InputCardNameModal);
  const {
    spaceId,
    billingInfo,
    spaceHashId,
    handleIsRegisterCard,
    handleStep,
    step,
    handleSpaceBilling
  } = useSubscriptionPaymentContext();
  const onSuccessRegisterBilling = usePreservedCallback(() => {
    handleStep(2);
    inputCardNameModal.show({
      spaceHashId,
      onSuccess(cardName) {
        handleSpaceBilling({
          cardName
        });
        handleIsRegisterCard(true);
        inputCardNameModal.remove();
      }
    }).catch(console.error);
  });
  const onFailRegisterBilling = usePreservedCallback((message: string) => {
    handleStep(2);
    createPaymentFailureDialog(dialog, {
      ...failureMetadata,
      // URL 쿼리는 사용자가 고칠 수 있다. 화면이 확실히 아는 값을 마지막에 둬 덮이지 않게 한다.
      payment_flow: billingInfo ? 'card_change' : 'card_registration',
      space_id: spaceId ?? failureMetadata.space_id,
      pg_provider: 'payple'
    }).alert({
      title: t('subscription:payment.alertFailCardRegister.title'),
      content: t('subscription:payment.alertFailPay.descriptionTossPayment', {
        tosspaymentsErrorMessage: message ?? 'Processing Failure'
      })
    }).catch(console.error);
  });
  useEffect(() => {
    if (isSuccess) {
      onSuccessRegisterBilling();
    }
  }, [isSuccess, onSuccessRegisterBilling]);
  useEffect(() => {
    if (isFailure) {
      onFailRegisterBilling(failMessage);
      return;
    }
  }, [isFailure, failMessage, failureCount, onFailRegisterBilling]);
  return <>
      <PaymentLoading data-sentry-element="PaymentLoading" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <div className={S.wrapper}>
        <div className={S.container}>
          <div className={S.content}>
            <PlanTitle data-sentry-element="PlanTitle" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
            <SubscriptionPaymentSteps data-sentry-element="SubscriptionPaymentSteps" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
            {step === 1 ? <Step1 /> : <Step2 />}
          </div>
          <div className={S.action}>
            <PaymentSummary data-sentry-element="PaymentSummary" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
          </div>
        </div>
      </div>
    </>;
};
const Step1 = () => {
  return <>
      <PaymentPeriodRadio data-sentry-element="PaymentPeriodRadio" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <PlanSelectMaxCcu data-sentry-element="PlanSelectMaxCcu" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <SpacePlanSelectButtons data-sentry-element="SpacePlanSelectButtons" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
      <PlanCheckoutSpace data-sentry-element="PlanCheckoutSpace" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
    </>;
};
const Step2 = () => <PaymentPaypleCardRegister data-sentry-element="PaymentPaypleCardRegister" data-sentry-component="Step2" data-sentry-source-file="SubscriptionForeignPayment.tsx" />;
export const SubscriptionForeignPayment = (props: SubscriptionPaymentProviderProps) => {
  return <PaypleProvider data-sentry-element="PaypleProvider" data-sentry-component="SubscriptionForeignPayment" data-sentry-source-file="SubscriptionForeignPayment.tsx">
      <SubscriptionPaymentProvider {...props} data-sentry-element="SubscriptionPaymentProvider" data-sentry-source-file="SubscriptionForeignPayment.tsx">
        <AdditionalPaymentFormProvider zipCode={isNil(props.billingInfo?.zipCode) ? null : props.billingInfo?.zipCode.toString()} data-sentry-element="AdditionalPaymentFormProvider" data-sentry-source-file="SubscriptionForeignPayment.tsx">
          <Content data-sentry-element="Content" data-sentry-source-file="SubscriptionForeignPayment.tsx" />
        </AdditionalPaymentFormProvider>
      </SubscriptionPaymentProvider>
    </PaypleProvider>;
};